import { ChevronDown, ChevronRight, ChevronsDownUp, FileCode, Folder, Loader2, MoreHorizontal, RefreshCw, Search, X } from "lucide-react"
import { observer } from "mobx-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import type { FileBrowserManager, TreeNode } from "../store/managers/FileBrowserManager"
import { FilePathActions } from "./FilePathActions"

interface TreeItemProps {
    node: TreeNode
    selected: boolean
    onSelect: () => void
    onToggle: () => void
    onOpen: () => void
    compact: boolean
    workingDir: string
}

function TreeItem({ node, selected, onSelect, onToggle, onOpen, compact, workingDir }: TreeItemProps) {
    const handleClick = useCallback(() => {
        onSelect()
        if (node.isDir) {
            onToggle()
        } else {
            onOpen()
        }
    }, [node.isDir, onSelect, onToggle, onOpen])

    const handleDoubleClick = useCallback(() => {
        if (!node.isDir) {
            onOpen()
        }
    }, [node.isDir, onOpen])

    return (
        <div
            className={twMerge(
                "group flex w-full items-center pr-1 text-sm transition-colors",
                compact ? "min-h-11" : "min-h-7",
                selected ? "bg-primary/10 text-base-content" : "text-muted hover:text-base-content hover:bg-base-200"
            )}
            style={{ paddingLeft: `${node.depth * 12 + 8}px` }}
        >
            <button
                type="button"
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                className={twMerge("btn flex min-w-0 flex-1 items-center gap-1 text-left", compact ? "min-h-11 py-2" : "py-1")}
            >
                {node.isDir ? (
                    <>
                        {node.isLoading ? (
                            <Loader2 size={14} className="flex-shrink-0 text-muted animate-spin" />
                        ) : node.isExpanded ? (
                            <ChevronDown size={14} className="flex-shrink-0 text-muted" />
                        ) : (
                            <ChevronRight size={14} className="flex-shrink-0 text-muted" />
                        )}
                        <Folder size={14} className="flex-shrink-0 text-warning" />
                    </>
                ) : (
                    <>
                        <span className="w-[14px]" />
                        <FileCode size={14} className="flex-shrink-0 text-muted" />
                    </>
                )}
                <span className="truncate font-mono text-xs">{node.name}</span>
            </button>
            <FilePathActions path={node.path} workingDir={workingDir} isDirectory={node.isDir} compact={compact} />
        </div>
    )
}

interface SearchResultItemProps {
    fullPath: string
    untracked: boolean
    selected: boolean
    onSelect: () => void
    onOpen: () => void
    compact: boolean
    workingDir: string
}

function SearchResultItem({ fullPath, untracked, selected, onSelect, onOpen, compact, workingDir }: SearchResultItemProps) {
    return (
        <div
            className={twMerge(
                "group flex w-full items-center pr-1 text-sm transition-colors",
                compact ? "min-h-11" : "min-h-8",
                selected ? "bg-primary/10 text-base-content" : "text-muted hover:text-base-content hover:bg-base-200",
                untracked && !selected && "opacity-60 hover:opacity-100"
            )}
        >
            <button
                type="button"
                title={untracked ? "Not tracked by this repository" : undefined}
                onClick={() => {
                    onSelect()
                    onOpen()
                }}
                className={twMerge("btn flex min-w-0 flex-1 items-center gap-2 px-2 text-left", compact ? "min-h-11 py-2" : "py-1.5")}
            >
                <FileCode size={14} className="flex-shrink-0 text-muted" />
                <span className="truncate font-mono text-xs">{fullPath}</span>
            </button>
            <FilePathActions path={fullPath} workingDir={workingDir} compact={compact} />
        </div>
    )
}

interface TreeNavigatorProps {
    fileBrowser: FileBrowserManager
    className?: string
    onEscapeClose?: () => void
    onFileOpen?: () => void
    compact?: boolean
}

export const TreeNavigator = observer(function TreeNavigator({ fileBrowser, className, onEscapeClose, onFileOpen, compact = false }: TreeNavigatorProps) {
    const searchInputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const [optionsOpen, setOptionsOpen] = useState(false)
    const tree = fileBrowser.flattenedTree
    const searchItems = fileBrowser.searchDisplayItems
    const isSearching = fileBrowser.isSearching

    // Focus search on mount
    useEffect(() => {
        if (compact) return
        const timer = setTimeout(() => {
            searchInputRef.current?.focus()
        }, 50)
        return () => clearTimeout(timer)
    }, [compact])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return

            const items = isSearching ? searchItems : tree

            const currentIndex = isSearching
                ? searchItems.findIndex((item) => item.fullPath === fileBrowser.selectedPath)
                : tree.findIndex((node) => node.path === fileBrowser.selectedPath)

            switch (e.key) {
                case "ArrowDown": {
                    e.preventDefault()
                    const nextIndex = Math.min(currentIndex + 1, items.length - 1)
                    if (isSearching) {
                        const item = searchItems[nextIndex]
                        if (item) fileBrowser.selectPath(item.fullPath)
                    } else {
                        const node = tree[nextIndex]
                        if (node) fileBrowser.selectPath(node.path)
                    }
                    break
                }
                case "ArrowUp": {
                    e.preventDefault()
                    const prevIndex = Math.max(currentIndex - 1, 0)
                    if (isSearching) {
                        const item = searchItems[prevIndex]
                        if (item) fileBrowser.selectPath(item.fullPath)
                    } else {
                        const node = tree[prevIndex]
                        if (node) fileBrowser.selectPath(node.path)
                    }
                    break
                }
                case "Enter": {
                    e.preventDefault()
                    if (isSearching) {
                        const item = searchItems.find((i) => i.fullPath === fileBrowser.selectedPath)
                        if (item) {
                            fileBrowser.openFile(item.fullPath)
                            onFileOpen?.()
                        }
                    } else {
                        const node = tree.find((n) => n.path === fileBrowser.selectedPath)
                        if (node) {
                            if (node.isDir) {
                                fileBrowser.toggleExpanded(node.path)
                            } else {
                                fileBrowser.openFile(node.path)
                                onFileOpen?.()
                            }
                        }
                    }
                    break
                }
                case "ArrowRight": {
                    if (!isSearching) {
                        e.preventDefault()
                        const node = tree.find((n) => n.path === fileBrowser.selectedPath)
                        if (node?.isDir && !node.isExpanded) {
                            fileBrowser.toggleExpanded(node.path)
                        }
                    }
                    break
                }
                case "ArrowLeft": {
                    if (!isSearching) {
                        e.preventDefault()
                        const node = tree.find((n) => n.path === fileBrowser.selectedPath)
                        if (node?.isDir && node.isExpanded) {
                            fileBrowser.toggleExpanded(node.path)
                        }
                    }
                    break
                }
                case "Escape":
                    if (fileBrowser.searchQuery) {
                        e.preventDefault()
                        fileBrowser.setSearchQuery("")
                    } else {
                        onEscapeClose?.()
                    }
                    break
            }
        },
        [fileBrowser, isSearching, onEscapeClose, onFileOpen, searchItems, tree]
    )

    // Scroll selected into view
    useEffect(() => {
        const listEl = listRef.current
        if (!listEl || !fileBrowser.selectedPath) return

        const selectedEl = listEl.querySelector(`[data-path="${CSS.escape(fileBrowser.selectedPath)}"]`)
        if (selectedEl) {
            selectedEl.scrollIntoView({ block: "nearest" })
        }
    }, [fileBrowser.selectedPath, tree.length, searchItems.length, isSearching])

    return (
        <div className={twMerge("flex flex-col h-full bg-base-100 border-r border-border", className)} onKeyDown={handleKeyDown}>
            {/* Search bar */}
            <div className="flex min-h-12 items-center gap-2 border-b border-border px-2 py-2">
                <Search size={14} className="text-muted flex-shrink-0" />
                <input
                    ref={searchInputRef}
                    type="text"
                    value={fileBrowser.searchQuery}
                    onChange={(e) => fileBrowser.setSearchQuery(e.target.value)}
                    placeholder="Search files..."
                    className="input flex-1 bg-transparent border-none text-sm px-0 py-1 focus:outline-none"
                />
                {fileBrowser.searchQuery && (
                    <button
                        type="button"
                        onClick={() => fileBrowser.setSearchQuery("")}
                        className="btn p-1 text-muted hover:text-base-content hover:bg-base-200 transition-colors"
                        title="Clear search"
                    >
                        <X size={14} />
                    </button>
                )}
                {compact ? (
                    <button
                        type="button"
                        onClick={() => setOptionsOpen((value) => !value)}
                        className="btn flex h-10 w-10 items-center justify-center text-muted hover:text-base-content hover:bg-base-200 transition-colors"
                        aria-label="File options"
                        aria-expanded={optionsOpen}
                    >
                        <MoreHorizontal size={17} />
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={() => fileBrowser.refreshTree()}
                            className="btn p-1 text-muted hover:text-base-content hover:bg-base-200 transition-colors"
                            title="Refresh file tree"
                        >
                            <RefreshCw size={14} />
                        </button>
                        <button
                            type="button"
                            onClick={() => fileBrowser.collapseAll()}
                            className="btn p-1 text-muted hover:text-base-content hover:bg-base-200 transition-colors"
                            title="Collapse all"
                        >
                            <ChevronsDownUp size={14} />
                        </button>
                    </>
                )}
            </div>
            {compact && optionsOpen && (
                <div className="flex shrink-0 items-center gap-2 border-b border-border bg-base-200 px-3 py-2">
                    <button
                        type="button"
                        onClick={() => {
                            fileBrowser.refreshTree()
                            setOptionsOpen(false)
                        }}
                        className="btn flex min-h-10 items-center gap-2 px-3 text-sm text-base-content hover:bg-base-300"
                    >
                        <RefreshCw size={15} />
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            fileBrowser.collapseAll()
                            setOptionsOpen(false)
                        }}
                        className="btn flex min-h-10 items-center gap-2 px-3 text-sm text-base-content hover:bg-base-300"
                    >
                        <ChevronsDownUp size={15} />
                        Collapse
                    </button>
                </div>
            )}

            {/* Tree or search results */}
            <div ref={listRef} className="flex-1 overflow-y-auto">
                {isSearching ? (
                    searchItems.length === 0 && !fileBrowser.searchLoading ? (
                        <div className="flex items-center justify-center py-8 text-muted text-sm">No files found</div>
                    ) : (
                        <div className="flex flex-col py-1">
                            {searchItems.map((item, index) => (
                                <div key={item.fullPath}>
                                    {item.untracked && !searchItems[index - 1]?.untracked && (
                                        <div className="mt-1 border-y border-border/70 bg-base-200/40 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted">
                                            Not tracked by this repo
                                        </div>
                                    )}
                                    <SearchResultItem
                                        fullPath={item.fullPath}
                                        untracked={item.untracked}
                                        selected={fileBrowser.selectedPath === item.fullPath}
                                        onSelect={() => fileBrowser.selectPath(item.fullPath)}
                                        onOpen={() => {
                                            fileBrowser.openFile(item.fullPath)
                                            onFileOpen?.()
                                        }}
                                        compact={compact}
                                        workingDir={fileBrowser.workingDir}
                                    />
                                </div>
                            ))}
                        </div>
                    )
                ) : tree.length === 0 ? (
                    <div className="flex items-center justify-center py-8 text-muted text-sm">No files</div>
                ) : (
                    <div className="flex flex-col py-1">
                        {tree.map((node) => (
                            <div key={node.path} data-path={node.path}>
                                <TreeItem
                                    node={node}
                                    selected={fileBrowser.selectedPath === node.path}
                                    onSelect={() => fileBrowser.selectPath(node.path)}
                                    onToggle={() => fileBrowser.toggleExpanded(node.path)}
                                    onOpen={() => {
                                        fileBrowser.openFile(node.path)
                                        onFileOpen?.()
                                    }}
                                    compact={compact}
                                    workingDir={fileBrowser.workingDir}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
})
